import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Lock, CreditCard, Gift, LogOut } from 'lucide-react';
import Button from '../../components/ui/Button';
import { Card, CardContent } from '../../components/ui/Card';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { refreshUsage } from '../../hooks/useUsage';

const SubscriptionPendingPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, refreshUser, logout } = useAuth() as any;
  const [isLoading, setIsLoading] = useState(false);
  const [action, setAction] = useState<'pay' | 'free' | null>(null);

  const currentSub = (user?.subscriptions || []).find((s: any) => ['trialing', 'active', 'past_due', 'unpaid', 'incomplete'].includes(s.status));
  const planName = currentSub?.plan?.name || 'pago';

  const handleResumeCheckout = async () => {
    if (!currentSub?.plan_id) return;
    setAction('pay');
    setIsLoading(true);
    try {
      const response = await api.post('/subscription/checkout', {
        planId: currentSub.plan_id,
        billingCycle: currentSub.billing_cycle || 'monthly',
        currency: 'brl'
      });
      const url = response.data?.data?.url;
      if (url) {
        window.location.href = url;
      } else {
        toast.error('Não foi possível iniciar o pagamento');
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Erro ao iniciar pagamento');
    } finally {
      setIsLoading(false);
      setAction(null);
    }
  };

  const handleDowngradeToFree = async () => {
    setAction('free');
    setIsLoading(true);
    try {
      await api.post('/subscription/downgrade-to-free');
      await refreshUser();
      await refreshUsage();
      toast.success('Plano alterado para Free');
      navigate('/dashboard', { replace: true });
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Erro ao mudar para Free');
    } finally {
      setIsLoading(false);
      setAction(null);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
      <Card className="max-w-md w-full">
        <CardContent className="pt-8 pb-8">
          <div className="text-center">
            <div className="w-16 h-16 bg-yellow-100 dark:bg-yellow-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
              <Lock className="w-10 h-10 text-yellow-600" />
            </div>

            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Cadastro de pagamento pendente
            </h1>

            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Você escolheu o plano <strong>{planName}</strong> mas ainda não finalizou o cadastro do cartão.
              Para acessar a plataforma, conclua o pagamento ou troque para o plano Free.
            </p>

            <div className="space-y-3">
              <Button
                className="w-full"
                onClick={handleResumeCheckout}
                isLoading={isLoading && action === 'pay'}
                disabled={isLoading}
                leftIcon={<CreditCard className="w-4 h-4" />}
              >
                Cadastrar cartão e continuar
              </Button>

              <Button
                variant="secondary"
                className="w-full"
                onClick={handleDowngradeToFree}
                isLoading={isLoading && action === 'free'}
                disabled={isLoading}
                leftIcon={<Gift className="w-4 h-4" />}
              >
                Usar plano Free (sem cartão)
              </Button>

              <Button
                variant="ghost"
                className="w-full"
                onClick={() => { logout(); navigate('/login', { replace: true }); }}
                disabled={isLoading}
                leftIcon={<LogOut className="w-4 h-4" />}
              >
                Sair
              </Button>
            </div>

            <p className="text-sm text-gray-500 dark:text-gray-400 mt-6">
              Precisa de ajuda?{' '}
              <a href="mailto:support@devskin.com" className="text-primary-600 hover:underline">
                Fale com o suporte
              </a>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SubscriptionPendingPage;
